import * as THREE from '@phdsoft/three';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import { DomainEntity } from "../DomainEntity";
import { DocumentResource } from "../ResourceElements/document";
import { MaterialType } from "../ResourceElements/MaterialType";
import { GaugingElement } from '../SectionElements/InspectionElements/gaugingElement';
import { GaugingPoint } from '../SectionElements/InspectionElements/gaugingPoint';
import { LinearElement } from "../SectionElements/StructureElements/LinearElement";
import { StructuralElement } from "../SectionElements/StructureElements/StructuralElement";
import { SurfaceElement } from "../SectionElements/StructureElements/SurfaceElement";
import { WorkableElement } from "../SectionElements/WorkableElement";
import { StructureDegradationParameters } from 'src/app/degradation/models/degradation-curve';
import { SectionBorderSettings } from './section-border-settings';


export class Section extends DomainEntity {

    private _isStructureSection: boolean = false;
    public get isStructureSection(): boolean {
        return this._isStructureSection;
    }
    public set isStructureSection(v: boolean) {
        this._isStructureSection = v;
    }


    public get isProtectionSection(): boolean {
        return this.isProtectionSection;
    }
    public set isProtectionSection(v: boolean) {
        this.isProtectionSection = v;
    }


    private _group: THREE.Group;
    public get group(): THREE.Group {
        return this._group;
    }
    public set group(v: THREE.Group) {
        this._group = v;
    }


    private _box3: THREE.Box3 = new THREE.Box3;
    public get box3(): THREE.Box3 {
        return this._box3;
    }
    public set box3(v: THREE.Box3) {
        this._box3 = v;
    }


    private _selected: boolean;
    public get selected(): boolean {
        return this._selected;
    }
    public set selected(v: boolean) {
        this._selected = v;
    }


    private _cssGroup: THREE.Group;
    public get cssGroup(): THREE.Group {
        return this._cssGroup;
    }
    public set cssGroup(v: THREE.Group) {
        this._cssGroup = v;
    }

    private _surfaceGroup: THREE.Group;
    public get surfaceGroup(): THREE.Group {
        return this._surfaceGroup;
    }
    public set surfaceGroup(v: THREE.Group) {
        this._surfaceGroup = v;
    }

    private _linearGroup: THREE.Group;
    public get linearGroup(): THREE.Group {
        return this._linearGroup;
    }
    public set linearGroup(v: THREE.Group) {
        this._linearGroup = v;
    }

    private _borderGroup: THREE.Group;
    public get borderGroup(): THREE.Group {
        return this._borderGroup;
    }
    public set borderGroup(v: THREE.Group) {
        this._borderGroup = v;
    }

    private _lightSectionGroup: THREE.Group;
    public get lightSectionGroup(): THREE.Group {
        return this._lightSectionGroup;
    }
    public set lightSectionGroup(v: THREE.Group) {
        this._lightSectionGroup = v;
    }

    public get visible(): boolean {
        return this.group.visible && this.group.parent?.visible && this.group.parent?.parent?.visible;
    }
    public set visible(v: boolean) {
        this.group.visible = v;
    }


    private _sectionDocuments: Array<DocumentResource> = new Array<DocumentResource>();
    public get sectionDocuments(): Array<DocumentResource> {
        return this._sectionDocuments;
    }
    public set sectionDocuments(v: Array<DocumentResource>) {
        this._sectionDocuments = v;
    }

    private _surfaceElements: Array<WorkableElement> = new Array<WorkableElement>();
    public get surfaceElements(): Array<WorkableElement> {
        return this._surfaceElements;
    }
    public set surfaceElements(v: Array<WorkableElement>) {
        this._surfaceElements = v;
    }

    private _linearElements: Array<WorkableElement> = new Array<WorkableElement>();
    public get linearElements(): Array<WorkableElement> {
        return this._linearElements;
    }
    public set linearElements(v: Array<WorkableElement>) {
        this._linearElements = v;
    }

    public set GroupsVisibility(v: boolean) {
        this.group.visible = v;
        this.surfaceGroup.visible = v;
        this.linearGroup.visible = v;
        if (this._borderGroup) this._borderGroup.visible = v;
    }

    private _sectionNormal: THREE.Vector3 = new THREE.Vector3();
    public get sectionNormal(): THREE.Vector3 {
        return this._sectionNormal;
    }
    public set sectionNormal(v: THREE.Vector3) {
        this._sectionNormal = v;
    }

    constructor(sectionId: string, sectionName: string) {
        super();
        this._group = new THREE.Group();
        this._cssGroup = new THREE.Group();
        this._linearGroup = new THREE.Group();
        this._linearGroup.visible = false;
        this._surfaceGroup = new THREE.Group();
        this._borderGroup = new THREE.Group();
        this._lightSectionGroup = new THREE.Group();

        this._group.add(this._surfaceGroup);
        this._group.add(this._linearGroup);
        this._group.add(this._borderGroup);
        this._group.add(this._lightSectionGroup);

        this.Guid = sectionId;
        this.Name = sectionName;

        this._group.name = this.Guid;
        this._linearGroup.name = this.Guid;
        this._surfaceGroup.name = this.Guid;
    }

    public addSurfaceElement(element: WorkableElement, addToGroup: boolean = true): void {
        this._surfaceElements.push(element);
        if (element instanceof WorkableElement) {
            if (addToGroup && element.SurfaceDesign instanceof THREE.Object3D) {
                this.surfaceGroup.add((element as WorkableElement).SurfaceDesign);
            }
            element.section = this;

            if (element.elementNormal && element.SurfaceDesign instanceof THREE.Object3D) {
                const elementNormal = new THREE.Vector3(Math.abs(element.elementNormal.x), Math.abs(element.elementNormal.y), Math.abs(element.elementNormal.z))
                this._sectionNormal.add(elementNormal);
                this._box3.expandByObject(element.SurfaceDesign);
            }
        }
    }

    public addSurfaceCSSElement(element: WorkableElement): void {
        if (element instanceof WorkableElement) {
            this.cssGroup.add((element as WorkableElement).css3dObject);
        }
    }

    public addLinearElement(element: WorkableElement, target: WorkableElement = null, addToGroup: boolean = true): void {
        this._linearElements.push(element);
        if (element instanceof WorkableElement) {
            if (addToGroup) {
                this.linearGroup.add((element as WorkableElement).SurfaceDesign);
            }
            element.section = this;
            if(target)
                this.linearGroup.visible = target?.section.linearGroup.visible;
        }
    }

    public get ElementsCount(): number {
        return this._linearElements.length + this.surfaceElements.length;
    }

    public computeSectionNormal(): void {
        for (const surfaceElement of this._surfaceElements) {
            if (surfaceElement.elementNormal) {
                const newNormal = new THREE.Vector3(Math.abs(surfaceElement.elementNormal.x),
                    Math.abs(surfaceElement.elementNormal.y), Math.abs(surfaceElement.elementNormal.z));
                this._sectionNormal.add(newNormal);
                this._box3.expandByObject(surfaceElement.SurfaceDesign);
            }
        }
        if (this._sectionNormal.equals(new THREE.Vector3())) {
            for (const surfaceElement of this._surfaceElements) {
                if (surfaceElement.elementNormal) {
                    this._sectionNormal.add(new THREE.Vector3(Math.abs(surfaceElement.elementNormal.x), Math.abs(surfaceElement.elementNormal.y),
                        Math.abs(surfaceElement.elementNormal.z)));
                }
            }
        }
        this._sectionNormal.normalize();
    }

    public renderBorders(material: THREE.Material): void {
        if (this._borderGroup) {
            this._borderGroup.visible = true;
        }
        for (const surfaceElement of this.surfaceElements) {
            if (surfaceElement instanceof GaugingPoint)
                return;
            (surfaceElement as SurfaceElement).createBorder(material);
        }
        for (const linearElement of this.linearElements) {
            (linearElement as LinearElement).createBorder(material);
        }
    }

    public clearBorders(): void {
        if (this._borderGroup) {
            this._borderGroup.visible = false;
        }
        for (const surfaceElement of this.surfaceElements) {
            (surfaceElement as SurfaceElement).clearBorder();
        }
    }

    /** Assign proper materials to LightSection merged meshes by element TypeId */
    public setLightSectionMaterial(): void {
        if (!this._lightSectionGroup) return;
        // Apply per-type materials in place. Type meshes stay SIBLINGS of
        // SurfaceTypesMesh (no reparenting) so their visibility can be toggled
        // independently via mesh.visible even after a vertex-color material swap.
        this._lightSectionGroup.traverse(child => {
            if (!(child instanceof THREE.Mesh)) return;
            this.applyTypeMaterial(child, child.name.split('_')[0]);
        });
    }

    private applyTypeMaterial(mesh: THREE.Mesh, typeName: string): void {
        switch (typeName) {
            case 'SurfaceTypesMesh':
                mesh.material = GraphicsMaterials.basicMaterial;
                mesh.renderOrder = 1;
                break;
            case ELEMENTSTYPESIDS.STIFFENER:
                mesh.material = GraphicsMaterials.stiffenerMaterial3d;
                mesh.renderOrder = 0;
                mesh.visible = false;
                break;
            case ELEMENTSTYPESIDS.FACEPLATE:
                mesh.material = GraphicsMaterials.flangeMaterial3d;
                mesh.renderOrder = 0;
                mesh.visible = false;
                break;
            case ELEMENTSTYPESIDS.BRACKET:
                mesh.material = GraphicsMaterials.bracketMaterial3d;
                mesh.renderOrder = 0;
                mesh.visible = false;
                break;
            case ELEMENTSTYPESIDS.BEAM:
                mesh.material = GraphicsMaterials.beamMaterial3d;
                mesh.renderOrder = 0;
                mesh.visible = false;
                break;
            default:
                mesh.material = GraphicsMaterials.basicMaterial;
                mesh.renderOrder = 1;
                mesh.visible = false;
                break;
        }
    }

    /** MODEL mode: show lightSection, hide surface/linear; border per the "show section borders" setting */
    public setViewOnModelMode(): void {
        this._surfaceGroup.visible = false;
        this._linearGroup.visible = false;
        // was: if (this._borderGroup) this._borderGroup.visible = false;
        this.applySectionBorderSetting();
        if (this._lightSectionGroup) {
            this._lightSectionGroup.visible = true;
            this.ensureSurfaceTypesMeshVisible();
        }
    }

    /** REGIONS mode: show lightSection, hide surface/linear; border per the "show section borders" setting */
    public setViewOnRegionMode(): void {
        this._surfaceGroup.visible = false;
        this._linearGroup.visible = false;
        // was: if (this._borderGroup) this._borderGroup.visible = true;
        this.applySectionBorderSetting();
        if (this._lightSectionGroup){
            this._lightSectionGroup.visible = true;
            this._lightSectionGroup.traverse(child => {
                if(child instanceof THREE.Mesh){
                    child.visible = true;
                }
            });
        } 
    }

    /**
     * New format, MODEL/REGIONS: show or hide the GLTF Borders group from the persisted
     * "show section borders" setting. Touches borderGroup.visible only (no other group).
     */
    public applySectionBorderSetting(): void {
        if (this._borderGroup) this._borderGroup.visible = SectionBorderSettings.show;
    }

    /** SECTIONS mode: show surface/linear, hide lightSection */
    public setViewOnSectionMode(): void {
        this._surfaceGroup.visible = true;
        this._linearGroup.visible = true;
        if (this._borderGroup) this._borderGroup.visible = true;
        if (this._lightSectionGroup) this._lightSectionGroup.visible = false;
    }

    private ensureSurfaceTypesMeshVisible(): void {
        if (!this._lightSectionGroup) return;

        this._lightSectionGroup.traverse(child => {
            if (!(child instanceof THREE.Mesh)) return;

            const meshName = child.name?.split('_')[0];
            if (meshName === 'SurfaceTypesMesh') {
                child.visible = true;
            }
        });
    }

    public setElementsProperties(elementsProperties: any, materials: Array<MaterialType>): void {
        for (const elementProperties of elementsProperties) {
            const surfaceElement = this._surfaceElements.find(x => (x as StructuralElement).Guid === elementProperties.ElementId.replace(/-/g, ''));
            if (surfaceElement) {
                surfaceElement.properties = elementProperties;
                if (elementProperties.General?.Collection && elementProperties.General?.Collection.Date) {
                    let formattedDate = new Date(new Date(elementProperties.General.Collection.Date).toDateString())
                    surfaceElement.date = formattedDate;
                }

                (surfaceElement as StructuralElement).thickness = surfaceElement.properties.Geometry?.Thickness;
                if (surfaceElement.properties.Geometry?.BaselineThickness > 0) {
                    (surfaceElement as StructuralElement).thickness = surfaceElement.properties.Geometry?.BaselineThickness;
                 }
                (surfaceElement as StructuralElement).ruleThickness = surfaceElement.properties.Geometry?.RuleThickness;
                (surfaceElement as StructuralElement).MaterialType = materials.find(x => x.Guid === elementProperties?.General?.MaterialId);
                (surfaceElement as StructuralElement).setComputedProperties((surfaceElement as StructuralElement).meanThicknessValue);
            }

            const linearElement = this._linearElements.find(x => (x as StructuralElement).Guid === elementProperties.ElementId.replace(/-/g, ''));
            if (linearElement) {
                if (elementProperties.General?.Collection && elementProperties.General?.Collection.Date) {
                    let formattedDate = new Date(new Date(elementProperties.General.Collection.Date).toDateString())
                    linearElement.date = formattedDate;
                }

                linearElement.properties = elementProperties;

                (linearElement as StructuralElement).thickness =
                    linearElement.properties.Geometry?.WebBaselineThickness > 0 ? linearElement.properties.Geometry?.WebBaselineThickness
                    : linearElement.properties.Geometry?.WebThickness > 0 ? linearElement.properties.Geometry?.WebThickness
                    : linearElement.properties.Geometry?.FlangeBaselineThickness > 0 ? linearElement.properties.Geometry?.FlangeBaselineThickness
                    : linearElement.properties.Geometry?.FlangeThickness > 0 ? linearElement.properties.Geometry?.FlangeThickness
                    : linearElement.properties.Geometry?.Thickness;


                (linearElement as StructuralElement).ruleThickness = linearElement.properties.Geometry?.RuleWebThickness > 0 ?
                    linearElement.properties.Geometry?.RuleWebThickness : linearElement.properties.Geometry?.RuleFlangeThickness > 0 ?
                        linearElement.properties.Geometry?.RuleFlangeThickness :
                        linearElement.properties.Geometry?.RuleThickness;
                (linearElement as StructuralElement).MaterialType = materials.find(x => x.Guid === elementProperties.General?.MaterialId) ?? null;
                (linearElement as StructuralElement).setComputedProperties((linearElement as StructuralElement).meanThicknessValue);
            }
        }
    }

    public clearElementsProperties(): void {
        for (const surfaceProperties of this._surfaceElements) {
            surfaceProperties.properties = null;
        }
        for (const linearProperties of this._linearElements) {
            linearProperties.properties = null;
        }
    }

    public getElementById(id: string): WorkableElement {
        let element = this._surfaceElements.find(x => x.Guid === id);
        if (element) return element;
        element = this._linearElements.find(x => x.Guid === id);
        if (element) return element;
        return null;
    }

    /**
     * Removes the element from this section (arrays + THREE group) WITHOUT
     * disposing its geometry, so it can be re-attached to another section/region.
     * Returns the detached element, or null when it is not in this section.
     */
    public detachElementByIdOnly(id: string): WorkableElement {
        const surfaceIndex = this._surfaceElements.findIndex(x => x.Guid === id);
        if (surfaceIndex > -1) {
            const element = this._surfaceElements[surfaceIndex];
            element.SurfaceDesign?.parent?.remove(element.SurfaceDesign);
            this._surfaceElements.splice(surfaceIndex, 1);
            return element;
        }
        const linearIndex = this._linearElements.findIndex(x => x.Guid === id);
        if (linearIndex > -1) {
            const element = this._linearElements[linearIndex];
            element.SurfaceDesign?.parent?.remove(element.SurfaceDesign);
            this._linearElements.splice(linearIndex, 1);
            return element;
        }
        return null;
    }

    public deleteElementByIdOnly(id: string): void {
        let elementIndex = this._surfaceElements.findIndex(x => x.Guid === id);
        if (elementIndex > -1) {
            const element = this._surfaceElements[elementIndex];
            if (element.SurfaceDesign.parent) {
                element.SurfaceDesign.parent.remove(element.SurfaceDesign);
            }
            if (element.SurfaceDesign.geometry) {
                element.SurfaceDesign.geometry.dispose();
            }
            this._surfaceElements.splice(elementIndex, 1);
            if (element instanceof GaugingElement) {
                const e = element as GaugingElement;
                const index = (e.targetElement as StructuralElement).gaugingElements.findIndex(x => x.Guid == e.Guid)
                if (index >= 0) {
                    (e.targetElement as StructuralElement).gaugingElements.splice(index, 1)
                }
            }
        }
    }

    public deleteLinearElementByIdOnly(id: string): void {
        let elementIndex = this._linearElements.findIndex(x => x.Guid === id);
        if (elementIndex > -1) {
            const element = this._linearElements[elementIndex];
            element.SurfaceDesign.parent.remove(element.SurfaceDesign);
            element.SurfaceDesign.geometry.dispose();
            this._linearElements.splice(elementIndex, 1);
            if (element instanceof GaugingElement) {
                const e = element as GaugingElement;
                const index = (e.targetElement as LinearElement).gaugingElements.findIndex(x => x.Guid == e.Guid)
                if (index >= 0) {
                    (e.targetElement as LinearElement).gaugingElements.splice(index, 1)
                }
            }
        }
    }

    public getVisibleBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        this._group.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                box.expandByObject(child);
            }
        });
        return box;
    }

    public updateDegradationColors(curves: StructureDegradationParameters[]): void {
        for (const surfaceProperties of this._surfaceElements) {
            if (surfaceProperties instanceof StructuralElement) {

                const curve = curves.find(c=> c.ParentGeometryId == surfaceProperties.Guid.replace(/-/g, ""));
                const estimatedThickness = curve?.EstimatedThickness;

                (surfaceProperties as StructuralElement).updateDegradationRate(estimatedThickness);
            }
        }
        for (const linearProperties of this._linearElements) {
            if (linearProperties instanceof StructuralElement) {

              const curve = curves.find(c=> c.ParentGeometryId == linearProperties.Guid.replace(/-/g, ""));
              const estimatedThickness = curve?.EstimatedThickness;

              (linearProperties as StructuralElement).updateDegradationRate(estimatedThickness);
            }
        }
    }

    public updateRemainingLifeViewMode(low: number, high: number, curves: StructureDegradationParameters[]): void {
        for (const surfaceProperties of this._surfaceElements) {
            if (surfaceProperties instanceof StructuralElement) {

              const curve = curves.find(c=> c.ParentGeometryId == surfaceProperties.Guid.replace(/-/g, ""));
              const remainingLife = curve?.RemainingLife;

              (surfaceProperties as StructuralElement).updateRemainingLifeViewMode(low, high, remainingLife);
            }
        }
        for (const linearProperties of this._linearElements) {
            if (linearProperties instanceof StructuralElement) {

              const curve = curves.find(c=> c.ParentGeometryId == linearProperties.Guid.replace(/-/g, ""));
              const remainingLife = curve?.RemainingLife;

              (linearProperties as StructuralElement).updateRemainingLifeViewMode(low, high, remainingLife);
            }
        }
    }

    public updateCorrosionRateViewMode(low: number, high: number, curves: StructureDegradationParameters[]): void {
        for (const surfaceProperties of this._surfaceElements) {
            if (surfaceProperties instanceof StructuralElement) {

              const curve = curves.find(c=> c.ParentGeometryId == surfaceProperties.Guid.replace(/-/g, ""));
              const corrosionRate = curve?.CorrosionRate;

              (surfaceProperties as StructuralElement).updateCorrosionRateViewMode(low, high, corrosionRate);
            }
        }
        for (const linearProperties of this._linearElements) {
            if (linearProperties instanceof StructuralElement) {

              const curve = curves.find(c=> c.ParentGeometryId == linearProperties.Guid.replace(/-/g, ""));
              const corrosionRate = curve?.CorrosionRate;

              (linearProperties as StructuralElement).updateCorrosionRateViewMode(low, high, corrosionRate);
            }
        }
    }

    public updateCorrosionRiskIndexViewMode(low: number, high: number, curves: StructureDegradationParameters[]): void {
        for (const surfaceProperties of this._surfaceElements) {
            if (surfaceProperties instanceof StructuralElement) {
              const riskIndex = 1;

              const curve = curves.find(c=> c.ParentGeometryId == surfaceProperties.Guid.replace(/-/g, ""));
              const corrosionRiskIndex = curve?.AbsoluteDegradation * riskIndex;

              (surfaceProperties as StructuralElement).updateCorrosionRiskIndexViewMode(low, high, corrosionRiskIndex);
            }
        }
        for (const linearProperties of this._linearElements) {
            if (linearProperties instanceof StructuralElement) {
              const riskIndex = 1;

              const curve = curves.find(c=> c.ParentGeometryId == linearProperties.Guid.replace(/-/g, ""));
              const corrosionRiskIndex = curve?.AbsoluteDegradation * riskIndex;

              (linearProperties as StructuralElement).updateCorrosionRiskIndexViewMode(low, high, corrosionRiskIndex);
            }
        }
    }

    public updateRenderOrder(cameraPosition: THREE.Vector3) {
      let center = new THREE.Vector3
      this.box3.getCenter(center)

      for (const surfaceProperties of this._surfaceElements) {
        let distance = cameraPosition.distanceTo(center)
        surfaceProperties.SurfaceDesign.renderOrder = -distance
      }

      for (const linearProperties of this._linearElements) {
        let distance = cameraPosition.distanceTo(center)
        linearProperties.SurfaceDesign.renderOrder = -distance
      }
    }

    // public getCorrosionRateParameters(): number[] {
    //     var corrosionRateList: number[] = []
    //     for (const surfaceProperties of this._surfaceElements) {
    //         if (surfaceProperties instanceof StructuralElement) {
    //             let corrosionRate = (surfaceProperties as StructuralElement).CorrosionRate;
    //             if (corrosionRate) corrosionRateList.push(corrosionRate)
    //         }
    //     }
    //     for (const linearProperties of this._linearElements) {
    //         if (linearProperties instanceof StructuralElement) {
    //             let corrosionRate = (linearProperties as StructuralElement).CorrosionRate;
    //             if (corrosionRate) corrosionRateList.push(corrosionRate)
    //         }
    //     }
    //     return corrosionRateList
    // }

    public getStructureElementsTypes(): Array<string> {
        // use Set to speed up type checking
        let typesSet: Set<string> = new Set();
        let structureElements = this.surfaceElements.concat(this.linearElements);

        let elementsTypes: Array<string> = structureElements.reduce((typesArray, element) => {
            if (element.properties?.TypeName == null || typesSet.has(element.properties?.TypeName)) {
                return typesArray;
            }
            typesSet.add(element.properties.TypeName);
            typesArray.push(element.properties.TypeName);
            return typesArray;
        }, [])

        return elementsTypes;
    }

    public getCenter(): THREE.Vector3 {
        if (this._box3.isEmpty) {
        var center = new THREE.Vector3();
        this._box3.getCenter(center);
        return center;
        }
        return null;
        }
}
